import { format } from 'date-fns'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FaCalendarAlt,
  FaClipboard,
  FaExclamationTriangle,
  FaFolder,
  FaUserFriends,
} from 'react-icons/fa'
import { Link } from 'react-router-dom'
import { MainLayout } from '../../components/layout'

import { apiClient } from '../../api'
import './MyTasks.css'

interface AssignedCard {
  id: number
  title: string
  dueDate: string
  columnId: number
  columnName: string
  boardId: number
  boardName: string
  workspaceId: number
  workspaceName: string
  archived: boolean
  members: Array<{
    userId: number
    name: string
  }>
}

interface AssignedCardsResponse {
  content: AssignedCard[]
  totalElements: number
}

export const MyTasks: React.FC = () => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [cards, setCards] = useState<AssignedCard[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize] = useState(10)
  const [totalElements, setTotalElements] = useState(0)

  const fetchAssignedCards = async () => {
    setLoading(true)
    try {
      const response = await apiClient.get<AssignedCardsResponse>(
        '/cards/assigned',
        {
          params: { page: currentPage, size: pageSize },
        }
      )

      setCards(response.content)
      setTotalElements(response.totalElements)
    } catch (error) {
      console.error('Error fetching assigned cards:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAssignedCards()
  }, [currentPage])

  const totalPages = Math.ceil(totalElements / pageSize)

  const getDueDateStatus = (dateString: string) => {
    if (!dateString) return { color: 'gray', label: t('dates.noDueDate') }

    const dueDate = new Date(dateString)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    dueDate.setHours(0, 0, 0, 0)

    if (dueDate < today) return { color: 'red', label: t('dates.overdue') }
    if (dueDate.getTime() === today.getTime())
      return { color: 'orange', label: t('dates.today') }

    return { color: 'blue', label: t('dates.upcoming') }
  }

  return (
    <MainLayout title={t('myTasks.title')}>
      <div className="my-tasks-container">
        {loading ? (
          <div className="loading">{t('common.loading')}</div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="tasks-table">
                <thead>
                  <tr>
                    <th>{t('myTasks.taskTitle')}</th>
                    <th>{t('myTasks.dueDate')}</th>
                    <th>{t('myTasks.column')}</th>
                    <th>{t('myTasks.board')}</th>
                    <th>{t('myTasks.workspace')}</th>
                    <th>{t('myTasks.members')}</th>
                    {/* <th>{t('myTasks.actions')}</th> */}
                  </tr>
                </thead>
                <tbody>
                  {cards.map((card) => {
                    const dueStatus = getDueDateStatus(card.dueDate)

                    return (
                      <tr
                        key={card.id}
                        className={card.archived ? 'archived' : ''}
                      >
                        <td>
                          <Link
                            to={`/boards/${card.boardId}/cards/${card.id}`}
                            className="task-title"
                          >
                            {card.title}
                            {card.archived && (
                              <span className="archived-badge">
                                {t('myTasks.archived')}
                              </span>
                            )}
                          </Link>
                        </td>

                        <td>
                          <div className={`due-date ${dueStatus.color}`}>
                            <FaCalendarAlt />
                            {card.dueDate
                              ? format(new Date(card.dueDate), 'dd.MM.yyyy')
                              : '-'}
                            {dueStatus.color === 'red' && (
                              <FaExclamationTriangle />
                            )}
                          </div>
                        </td>

                        <td>
                          <div className="column-info">
                            <span className="column-dot"></span>
                            {card.columnName}
                          </div>
                        </td>

                        <td>
                          <Link
                            to={`/boards/${card.boardId}`}
                            className="board-link"
                          >
                            <FaClipboard />
                            {card.boardName}
                          </Link>
                        </td>

                        <td>
                          <Link
                            to={`/workspaces/${card.workspaceId}`}
                            className="workspace-link"
                          >
                            <FaFolder />
                            {card.workspaceName}
                          </Link>
                        </td>

                        <td>
                          <div className="members-list">
                            <FaUserFriends />
                            {card.members.map((member) => (
                              <span key={member.userId} className="member-tag">
                                {member.name}
                              </span>
                            ))}
                          </div>
                        </td>

                        {/* <td>
                          <Link
                            to={`/boards/${card.boardId}/cards/${card.id}`}
                            className="action-btn"
                          >
                            {t('common.open')}
                          </Link>
                        </td> */}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="pagination-section">
              <div className="pagination-info">
                {t('pagination.showing', {
                  from: currentPage * pageSize + 1,
                  to: Math.min((currentPage + 1) * pageSize, totalElements),
                  total: totalElements,
                })}
              </div>

              <div className="pagination-controls">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(0, prev - 1))
                  }
                  disabled={currentPage === 0}
                  className="pagination-btn"
                >
                  {t('pagination.prev')}
                </button>

                <span className="page-numbers">
                  {Array.from({ length: totalPages }, (_, i) => i).map(
                    (page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`page-btn ${currentPage === page ? 'active' : ''}`}
                      >
                        {page + 1}
                      </button>
                    )
                  )}
                </span>

                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))
                  }
                  disabled={currentPage >= totalPages - 1}
                  className="pagination-btn"
                >
                  {t('pagination.next')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </MainLayout>
  )
}

export default MyTasks
